import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MDRBadge } from "@/components/MDRBadge";
import {
  reportingActivityData, reportingSLA, getStructureById, getPharmacyById
} from "@/data/platformData";
import { doctorsData, getServiceById, getPatientById } from "@/data/mockData";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Cell
} from "recharts";
import {
  BarChart3, Clock, AlertTriangle, CheckCircle2, ArrowLeft, FileSignature, Activity, Timer
} from "lucide-react";

const STRUCTURE_ID = "str1";

const STATUS_LABEL = {
  pending: "In attesa",
  in_review: "In lavorazione",
  signed: "Firmato",
  sent: "Inviato"
};
const STATUS_STYLE = {
  pending: "bg-rose-50 text-rose-700 border-rose-200",
  in_review: "bg-amber-50 text-amber-700 border-amber-200",
  signed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  sent: "bg-sky-50 text-sky-700 border-sky-200"
};

const formatDuration = (ms) => {
  if (ms == null) return "—";
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

export const StructureMonitoringPage = () => {
  const navigate = useNavigate();
  const structure = getStructureById(STRUCTURE_ID);
  const activity = reportingActivityData.filter(r => r.structureId === STRUCTURE_ID);

  const stats = useMemo(() => {
    const pending = activity.filter(a => a.status === "pending").length;
    const inReview = activity.filter(a => a.status === "in_review").length;
    const signed = activity.filter(a => a.status === "signed").length;
    const completed = activity.filter(a => a.deliveredAt);
    const avgMs = completed.length
      ? completed.reduce((acc, c) => acc + (new Date(c.deliveredAt) - new Date(c.receivedAt)), 0) / completed.length
      : 0;
    const overdue = activity.filter(a => {
      if (a.deliveredAt) return false;
      const slaH = reportingSLA[a.serviceId] || 24;
      const elapsed = (Date.now() - new Date(a.receivedAt).getTime()) / 3600000;
      return elapsed > slaH;
    }).length;
    return { pending, inReview, signed, avgMs, overdue, total: activity.length };
  }, [activity]);

  // Per-doctor stats
  const perDoctor = doctorsData.map(d => {
    const list = activity.filter(a => a.specialistId === d.id);
    const done = list.filter(a => a.deliveredAt);
    const avg = done.length ? done.reduce((acc, c) => acc + (new Date(c.deliveredAt) - new Date(c.receivedAt)), 0) / done.length : 0;
    return {
      id: d.id,
      name: d.name.replace("Dr.", "").replace("Dr.ssa", "").trim(),
      pending: list.filter(a => a.status === "pending").length,
      inReview: list.filter(a => a.status === "in_review").length,
      signed: list.filter(a => a.status === "signed").length,
      avgHours: Number((avg / 3600000).toFixed(1))
    };
  }).filter(d => d.pending + d.inReview + d.signed > 0);

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-6">
      <div>
        <Button variant="ghost" size="sm" className="mb-2 -ml-2" onClick={() => navigate("/structure")}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Panoramica
        </Button>
        <div className="flex items-center gap-2 mb-1">
          <BarChart3 className="h-4 w-4 text-cyan-600" />
          <span className="text-xs uppercase tracking-wider text-muted-foreground">Monitoraggio refertazioni</span>
          <MDRBadge variant="admin" size="sm" />
        </div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Pipeline refertazione in tempo reale</h1>
        <p className="text-sm text-muted-foreground mt-1">{structure.name}</p>
      </div>

      {/* KPI strip */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-5">
        {[
          { label: "Totali", value: stats.total, icon: Activity, accent: "text-primary bg-primary/10" },
          { label: "In attesa", value: stats.pending, icon: Clock, accent: "text-rose-700 bg-rose-100" },
          { label: "In lavorazione", value: stats.inReview, icon: FileSignature, accent: "text-amber-700 bg-amber-100" },
          { label: "Tempo medio", value: formatDuration(stats.avgMs), icon: Timer, accent: "text-sky-700 bg-sky-100" },
          { label: "Fuori SLA", value: stats.overdue, icon: AlertTriangle, accent: "text-rose-700 bg-rose-100" }
        ].map(k => {
          const Icon = k.icon;
          return (
            <Card key={k.label}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${k.accent}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold leading-none">{k.value}</p>
                  <p className="text-xs mt-1 text-muted-foreground">{k.label}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Tabs defaultValue="queue" className="space-y-4">
        <TabsList>
          <TabsTrigger value="queue" data-testid="monitoring-tab-queue">Coda refertazioni</TabsTrigger>
          <TabsTrigger value="doctors" data-testid="monitoring-tab-doctors">Performance specialisti</TabsTrigger>
        </TabsList>

        <TabsContent value="queue" className="space-y-3">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <FileSignature className="h-4 w-4 text-cyan-600" /> Esami nel sistema
              </CardTitle>
              <p className="text-xs text-muted-foreground">SLA per servizio: ECG 24h · Spirometria 48h · Emocromo 24h · Colesterolo/HbA1c 48h</p>
            </CardHeader>
            <CardContent className="space-y-2">
              {activity.map(a => {
                const patient = getPatientById(a.patientId);
                const service = getServiceById(a.serviceId);
                const specialist = doctorsData.find(d => d.id === a.specialistId);
                const pharmacy = getPharmacyById(a.pharmacyId);
                const slaH = reportingSLA[a.serviceId] || 24;
                const elapsedH = (Date.now() - new Date(a.receivedAt).getTime()) / 3600000;
                const isOverdue = !a.deliveredAt && elapsedH > slaH;
                const ttr = a.deliveredAt ? new Date(a.deliveredAt).getTime() - new Date(a.receivedAt).getTime() : null;
                return (
                  <div key={a.id} className={`p-3 rounded-lg border bg-card ${isOverdue ? "border-rose-300" : ""}`} data-testid={`activity-row-${a.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-sm truncate">{patient?.name || a.patientId}</p>
                          <Badge variant="secondary" className="text-[10px]">{service?.name || a.serviceId}</Badge>
                          {a.urgency === "urgent" && <Badge variant="destructive" className="text-[10px]">Urgente</Badge>}
                          {isOverdue && <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 gap-1 text-[10px]"><AlertTriangle className="h-3 w-3" /> Fuori SLA</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {pharmacy?.name} → {specialist?.name} · ricevuto {new Date(a.receivedAt).toLocaleString("it-IT")}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge variant="outline" className={STATUS_STYLE[a.status]}>{STATUS_LABEL[a.status]}</Badge>
                        {ttr != null && <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><Timer className="h-3 w-3" /> {formatDuration(ttr)}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="doctors" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Refertazioni completate per specialista
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={perDoctor}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="name" fontSize={11} />
                    <YAxis fontSize={11} />
                    <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                    <Bar dataKey="pending" stackId="s" fill="hsl(var(--destructive))" name="In attesa" />
                    <Bar dataKey="inReview" stackId="s" fill="hsl(var(--warning))" name="In lavorazione" />
                    <Bar dataKey="signed" stackId="s" fill="hsl(var(--success))" name="Firmati" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-3 md:grid-cols-2">
            {perDoctor.map(d => (
              <Card key={d.id}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="font-semibold text-sm">{d.name}</p>
                    <Badge variant="secondary" className="font-mono">{d.signed} firmati</Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-2xl font-bold text-rose-600">{d.pending}</p>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Attesa</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-amber-600">{d.inReview}</p>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Lavorazione</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-emerald-600">{d.signed}</p>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Firmati</p>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Tempo medio refertazione</span>
                    <Badge variant="outline" className="gap-1"><Timer className="h-3 w-3" /> {d.avgHours}h</Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StructureMonitoringPage;
